import { create } from "zustand";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  deleteDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import type { Action, Emergency, ActionYear, Metric } from "@/types/report";
import { toast } from "@/hooks/use-toast";

interface ReportsState {
  actions: Action[];
  emergencies: Emergency[];
  metrics: Metric[];
  loading: boolean;
  error: string | null;

  fetchData: () => void;
  unsubscribe: () => void;

  addAction: (name: string) => Promise<string>;
  addYearToAction: (actionId: string, yearData: ActionYear) => Promise<void>;
  updateYear: (actionId: string, yearIndex: number, updatedYear: ActionYear) => Promise<void>;
  deleteAction: (actionId: string) => Promise<void>;
  clearActionYears: (actionId: string) => Promise<void>;

  addEmergency: (data: Omit<Emergency, "id"> & { photoFiles?: File[] }) => Promise<void>;
  updateEmergency: (id: string, updates: Partial<Omit<Emergency, "id">> & { photoFiles?: File[] }) => Promise<void>;
  deleteEmergency: (id: string) => Promise<void>;
  deletePhotoFromEmergency: (emergencyId: string, photoUrl: string) => Promise<void>;

  // === ФУНКЦИИ ДЛЯ КЛЮЧЕВЫХ ПОКАЗАТЕЛЕЙ ===
  addMetric: (name: string, initialValue: number) => Promise<void>;
  updateMetric: (id: string, newValue: number) => Promise<void>;
  deleteMetric: (id: string) => Promise<void>;        // ← НОВОЕ
}

let unsubscribeActions: (() => void) | null = null;
let unsubscribeEmergencies: (() => void) | null = null;
let unsubscribeMetrics: (() => void) | null = null;

export const useReportsStore = create<ReportsState>((set, get) => ({
  actions: [],
  emergencies: [],
  metrics: [],
  loading: true,
  error: null,

  fetchData: () => {
    set({ loading: true, error: null });

    const actionsQuery = collection(db, "actions");
    unsubscribeActions = onSnapshot(actionsQuery, (snap) => {
      set({ actions: snap.docs.map(d => ({ id: d.id, ...d.data() })) as Action[] });
    }, err => {
      console.error("Акции error:", err);
      set({ error: err.message });
      toast({ title: "Ошибка", description: "Не удалось загрузить акции", variant: "destructive" });
    });

    const emergenciesQuery = collection(db, "emergencies");
    unsubscribeEmergencies = onSnapshot(emergenciesQuery, (snap) => {
      set({ emergencies: snap.docs.map(d => ({ id: d.id, ...d.data() })) as Emergency[], loading: false });
    }, err => {
      console.error("ЧС error:", err);
      set({ error: err.message, loading: false });
      toast({ title: "Ошибка", description: "Не удалось загрузить ЧС", variant: "destructive" });
    });

    const metricsQuery = collection(db, "metrics");
    unsubscribeMetrics = onSnapshot(metricsQuery, (snap) => {
      set({ 
        metrics: snap.docs.map(d => ({ 
          id: d.id, 
          ...d.data() 
        })) as Metric[] 
      });
    }, err => {
      console.error("Metrics error:", err);
      set({ error: err.message });
      toast({ title: "Ошибка", description: "Не удалось загрузить показатели", variant: "destructive" });
    });
  },

  unsubscribe: () => {
    unsubscribeActions?.();
    unsubscribeEmergencies?.();
    unsubscribeMetrics?.();
    unsubscribeActions = null;
    unsubscribeEmergencies = null;
    unsubscribeMetrics = null;
  },

  addAction: async (name) => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error("Название акции обязательно");
    const ref = doc(collection(db, "actions"));
    await setDoc(ref, { name: trimmed, years: [] });
    return ref.id;
  },

  addYearToAction: async (actionId, yearData) => {
    await updateDoc(doc(db, "actions", actionId), { years: arrayUnion(yearData) });
  },

  updateYear: async (actionId, yearIndex, updatedYear) => {
    const action = get().actions.find(a => a.id === actionId);
    if (!action) throw new Error("Акция не найдена");
    const years = [...action.years];
    years[yearIndex] = updatedYear;
    await updateDoc(doc(db, "actions", actionId), { years });
  },

  deleteAction: async (actionId) => {
    await deleteDoc(doc(db, "actions", actionId));
    toast({ title: "Успех", description: "Акция удалена" });
  },

  clearActionYears: async (actionId) => {
    await updateDoc(doc(db, "actions", actionId), { years: [] });
    toast({ title: "Готово", description: "График очищен" });
  },

  addEmergency: async (data) => {
    try {
      const photoUrls: string[] = [];
      if (data.photoFiles?.length) {
        for (const file of data.photoFiles) {
          if (!file.type.startsWith("image/")) continue;
          const path = `emergencies/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
          const ref = storageRef(storage, path);
          await uploadBytes(ref, file);
          photoUrls.push(await getDownloadURL(ref));
        }
      }

      const { photoFiles, ...saveData } = data;
      const ref = doc(collection(db, "emergencies"));
      await setDoc(ref, { ...saveData, photoUrls, createdAt: new Date().toISOString() });

      toast({ title: "Успех", description: `ЧС добавлена (${photoUrls.length} фото)` });
    } catch (err: any) {
      console.error("addEmergency error:", err);
      toast({ title: "Ошибка", description: err.message || "Не удалось добавить", variant: "destructive" });
      throw err;
    }
  },

  updateEmergency: async (id, updates) => {
    try {
      let newPhotoUrls: string[] = [];

      if (updates.photoFiles && updates.photoFiles.length > 0) {
        const emergency = get().emergencies.find(e => e.id === id);
        const existing = emergency?.photoUrls || [];

        for (const file of updates.photoFiles) {
          if (!file.type.startsWith("image/")) continue;
          const path = `emergencies/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
          const ref = storageRef(storage, path);
          await uploadBytes(ref, file);
          const url = await getDownloadURL(ref);
          newPhotoUrls.push(url);
        }

        newPhotoUrls = [...existing, ...newPhotoUrls];
      }

      const { photoFiles, ...cleanUpdates } = updates;
      if (newPhotoUrls.length) cleanUpdates.photoUrls = newPhotoUrls;

      set(state => ({
        emergencies: state.emergencies.map(e =>
          e.id === id ? { ...e, ...cleanUpdates } : e
        ),
      }));

      const ref = doc(db, "emergencies", id);
      await updateDoc(ref, cleanUpdates);

      toast({ title: "Успех", description: "ЧС обновлена" });
    } catch (err: any) {
      console.error("updateEmergency error:", err);
      toast({ title: "Ошибка", description: err.message || "Не удалось обновить", variant: "destructive" });
      throw err;
    }
  },

  deleteEmergency: async (id) => {
    try {
      await deleteDoc(doc(db, "emergencies", id));
      toast({ title: "Удалено", description: "ЧС удалена" });
    } catch (err: any) {
      toast({ title: "Ошибка", description: "Не удалось удалить", variant: "destructive" });
      throw err;
    }
  },

  deletePhotoFromEmergency: async (emergencyId, photoUrl) => {
    try {
      const emergency = get().emergencies.find(e => e.id === emergencyId);
      if (!emergency?.photoUrls) throw new Error("Фото не найдено");

      const newUrls = emergency.photoUrls.filter(u => u !== photoUrl);

      await updateDoc(doc(db, "emergencies", emergencyId), { photoUrls: newUrls });

      set(state => ({
        emergencies: state.emergencies.map(e =>
          e.id === emergencyId ? { ...e, photoUrls: newUrls } : e
        ),
      }));

      toast({ title: "Фото удалено" });
    } catch (err: any) {
      toast({ title: "Ошибка", description: "Не удалось удалить фото", variant: "destructive" });
      throw err;
    }
  },

  // ==================== ФУНКЦИИ ДЛЯ КЛЮЧЕВЫХ ПОКАЗАТЕЛЕЙ ====================

  addMetric: async (name, initialValue) => {
    try {
      const ref = doc(collection(db, "metrics"));
      await setDoc(ref, {
        name: name.trim(),
        value: initialValue,
        updatedAt: new Date().toISOString(),
      });
      toast({ title: "Успех", description: "Показатель добавлен" });
    } catch (err: any) {
      console.error("addMetric error:", err);
      toast({ title: "Ошибка", description: err.message || "Не удалось добавить показатель", variant: "destructive" });
      throw err;
    }
  },

  updateMetric: async (id, newValue) => {
    try {
      const ref = doc(db, "metrics", id);
      await updateDoc(ref, { 
        value: newValue,
        updatedAt: new Date().toISOString() 
      });

      set(state => ({
        metrics: state.metrics.map(m =>
          m.id === id 
            ? { ...m, value: newValue, updatedAt: new Date().toISOString() } 
            : m
        )
      }));

      toast({ title: "Обновлено", description: "Значение показателя изменено" });
    } catch (err: any) {
      console.error("updateMetric error:", err);
      toast({ title: "Ошибка", description: err.message || "Не удалось обновить показатель", variant: "destructive" });
      throw err;
    }
  },

  // === НОВАЯ ФУНКЦИЯ УДАЛЕНИЯ ПОКАЗАТЕЛЯ ===
  deleteMetric: async (id) => {
    try {
      await deleteDoc(doc(db, "metrics", id));
      toast({ title: "Удалено", description: "Показатель успешно удалён" });
    } catch (err: any) {
      console.error("deleteMetric error:", err);
      toast({ title: "Ошибка", description: "Не удалось удалить показатель", variant: "destructive" });
      throw err;
    }
  },
}));